import { useDispatch, useSelector } from "react-redux";
import { NewOrdersDialogWrapper } from "../NewOrders.styles";
import { RootState } from "stores/application.store";
import { DialogTracking } from "components/Dialog/components/DialogTracking.ts";
import AccountBillToDialogForm from "../AccountBillToDialogForm";
import { updateOrderDetails } from "pages/OrderPages/utils/OrderDetailUtil";
import { useNavigate, useParams } from "react-router";
import CSVUpload from "../CSVUpload/CSVUpload.tsx";
import { newOrderActions } from "features/reducers/newOrder/newOrder";
import { models } from "types/api/viewModels.ts";
import BuildOrderOptionsDialog from "../BuildOrderOptionsDialog/BuildOrderOptionsDialog.tsx";
import { useUserLocation } from "hooks/useUserLocation.ts";
import { useDialog } from "hooks/useDialog.ts";

interface NewOrderDialog {
	orderDetails?: models["OrderDetailViewModel"];
	dialogStatus: DialogTracking;
	isInternalDesigner?: boolean;
}

const NewOrdersDialog = ({ orderDetails, dialogStatus, isInternalDesigner }: NewOrderDialog) => {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const params = useParams();
	const { isNewOrderLandingPage, isAddOnLandingPage, isAddOnPage, isTrucksPage, isOrderDetailsPage, isNewQuotePage } =
		useUserLocation();
	const { closeDialog } = useDialog();
	const dialogStatusCSV = dialogStatus === DialogTracking.CSV_UPLOAD;
	const dialogStatusCancelOrder = dialogStatus === DialogTracking.CANCEL_ORDER;
	const dialogStatusAccount = dialogStatus === DialogTracking.SELECT_BILLTO_ACCOUNT;
	const dialogStatusBuildOrderOptions = dialogStatus === DialogTracking.BUILD_ORDER_OPTIONS;
	const dialogStatusTrucks = isTrucksPage;
	const parsedCSV = useSelector((state: RootState) => state.newOrder.parsedCSV);
	const tempCSVContents = useSelector((state: RootState) => state.newOrder.parsedTempCSV);
	const csvContents = tempCSVContents ?? parsedCSV;

	const onClose = () => {
		if (isNewOrderLandingPage || isAddOnLandingPage || isOrderDetailsPage || isInternalDesigner) {
			dispatch(newOrderActions.clearNewOrder());
		}
		closeDialog();
	};

	const draftOrder = useSelector((state: RootState) => state.newOrder.draftDetails?.draftOrder);

	const handleStartOver = () => {
		dispatch(newOrderActions.clearNewOrder());
		closeDialog();
	};

	const handleModalContinue = () => {
		const csvConfigurations = csvContents
			?.map((file) =>
				file?.configurations?.map((config) => {
					if (isOrderDetailsPage && orderDetails?.isCustomColorOrderEligible) {
						return config.globals
							? {
									...config,
									globals: orderDetails.configurations?.[0]?.globals
								}
							: config;
					}
					return config ?? [];
				})
			)
			.filter((file) => typeof file !== "undefined")
			.flatMap((file) => file);
		updateOrderDetails(
			[
				...(!isOrderDetailsPage && Array.isArray(draftOrder?.configurations) ? draftOrder.configurations : []),
				...(csvConfigurations ?? [])
			],
			dispatch,
			draftOrder,
			isAddOnPage,
			params.orderId
		);

		if (isAddOnPage) {
			navigate(`/add-ons/build-order/${params.orderId}`);
		} else if (isNewQuotePage || isInternalDesigner) {
			navigate("/new-quote/build-order");
		} else {
			navigate("/new-order/build-order");
		}
		closeDialog();
		if (tempCSVContents) {
			dispatch(newOrderActions.overwriteParsedCSV(tempCSVContents));
			dispatch(newOrderActions.addMultiConfigsToOrder(tempCSVContents.flatMap((e) => e?.configurations ?? [])));
		}
	};

	return (
		<NewOrdersDialogWrapper
			open={
				dialogStatusCSV ||
				dialogStatusAccount ||
				dialogStatusCancelOrder ||
				dialogStatusBuildOrderOptions ||
				dialogStatusTrucks
			}
			onClose={onClose}
			disableEscapeKeyDown={dialogStatusAccount}
			isCSVModal={dialogStatusCSV}
			isCancelModal={dialogStatusCancelOrder}
			isRemoveTruckModal={dialogStatusTrucks}
		>
			{dialogStatusAccount && (
				<AccountBillToDialogForm
					handleFindOriginalOrder={handleStartOver}
					handleModalContinue={handleModalContinue}
					csvContents={csvContents}
					isAddOn={isAddOnPage}
					orderDetails={orderDetails}
				/>
			)}

			{dialogStatusCSV && <CSVUpload handleClose={onClose} />}

			{dialogStatusBuildOrderOptions && (
				<BuildOrderOptionsDialog
					onClose={onClose}
					isNewQuoteOrder={isInternalDesigner}
				/>
			)}
		</NewOrdersDialogWrapper>
	);
};

export default NewOrdersDialog;
